// File: frontend/src/components/admin/AdminStadiums.tsx
// src/components/admin/AdminStadiums.tsx

import { Link } from "react-router-dom";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaPlus,
  FaEdit,
  FaTrash,
  FaBuilding,
  FaChevronDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaToggleOn,
  FaToggleOff,
  FaMapMarkerAlt,
  FaStar,
} from "react-icons/fa";

import { AdminStadiumModel, AdminStadiumSurface, CreateAdminStadiumRequest, UpdateAdminStadiumRequest } from "../../models/AdminStadiumModel";
import {
  AdminStadiumFilters,
  getAdminStadiums,
  createAdminStadium,
  updateAdminStadium,
  deleteAdminStadium,
  toggleStadiumActive,
} from "../../services/adminStadiumService";
import { TeamModel, getTeamUrlSlug } from "../../models/TeamModel";
import { getAdminTeams } from "../../services/adminTeamService";
import { Pagination } from "../../utils/Pagination";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner, AdminErrorBanner } from "../common/AdminBanner";
import {
  ADMIN_PAGE_SIZE,
  DROPDOWN_BLUR_DELAY_MS,
  ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH,
  ADMIN_STADIUM_NAME_MAX_LENGTH,
  ADMIN_STADIUM_SHORT_NAME_MAX_LENGTH,
  ADMIN_STADIUM_NICKNAME_MAX_LENGTH,
  ADMIN_STADIUM_ADDRESS_MAX_LENGTH,
  ADMIN_CITY_MAX_LENGTH,
  ADMIN_URL_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { InputClearButton } from "../common/InputClearButton";
import { AdminCharCounter } from "./common";
import { SURFACE_OPTIONS } from "../../constants/admin.constants";
import { MdCountryDto, getPublicCountries } from "../../services/masterDataService";
import "./AdminStadiums.css";


interface AdminStadiumsProps {
  isAdmin: boolean;
}

const surfaceLabel = (v: string | null) =>
  SURFACE_OPTIONS.find(o => o.value === v)?.i18nKey ?? null;

// Returns CSS modifier class for surface badge — must match AdminStadiums.css
const surfaceBadgeClass = (surface: string) =>
  `surface-badge surface-${surface.toLowerCase()}`;

export const AdminStadiums: React.FC<AdminStadiumsProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  // Countries loaded from master data API — replaces hardcoded COUNTRY_OPTIONS
  const [countries, setCountries] = useState<MdCountryDto[]>([]);
  useEffect(() => {
    getPublicCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  // ── Data state ────────────────────────────────────────────────
  const [stadiums, setStadiums]     = useState<AdminStadiumModel[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();

  // ── Pagination state ──────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(0);
  const [totalItems, setTotalItems]   = useState(0);

  // ── Filter state ──────────────────────────────────────────────
  const [filters, setFilters] = useState<AdminStadiumFilters>({
    search: "", country: "", active: "", isPrimary: "", teamId: "",
    sortBy: "name", sortDir: "asc",
  });
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // ── Modal state ───────────────────────────────────────────────
  const [modalType, setModalType] = useState<"create" | "edit" | "delete" | null>(null);
  const [selectedStadium, setSelectedStadium] = useState<AdminStadiumModel | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // ── Form state ────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    teamId:        0,
    name:          "",
    shortName:     "",
    capacity:      "" as string | number,
    builtYear:     "" as string | number,
    renovatedYear: "" as string | number,
    surface:       "" as AdminStadiumSurface | "",
    isPrimary:     false,
    city:          "",
    country:       "",
    address:       "",
    latitude:      "" as string | number,
    longitude:     "" as string | number,
    imageUrl:      "",
    nickname:      "",
    active:        true,
  });

  // ── Team search for create modal ──────────────────────────────
  const [allTeams, setAllTeams]           = useState<TeamModel[]>([]);
  const [teamSearch, setTeamSearch]       = useState("");
  const [selectedTeam, setSelectedTeam]   = useState<TeamModel | null>(null);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);

  // Load all active teams once for the dropdown
  useEffect(() => {
    getAdminTeams(0, 500, {
      search: "", sportType: "", gender: "", active: "true",
      clubId: "", country: "", sortBy: "clubShortName", sortDir: "asc",
    })
      .then(res => setAllTeams(res.content))
      .catch(err => console.error("Failed to load teams:", err));
  }, []);

  // Filtered team results — show all when search is empty, filter when typing
  const filteredTeams = React.useMemo(() => {
    if (selectedTeam) return [];
    const q = teamSearch.toLowerCase().trim();
    const list = q
      ? allTeams.filter(t =>
          t.clubShortName.toLowerCase().includes(q) ||
          (t.displayName && t.displayName.toLowerCase().includes(q)) ||
          t.sportType.toLowerCase().includes(q)
        )
      : allTeams;
    return list.slice(0, 12);
  }, [teamSearch, allTeams, selectedTeam]);

  // ── Fetch stadiums ────────────────────────────────────────────
  const fetchStadiums = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAdminStadiums(currentPage, ADMIN_PAGE_SIZE, filters);
      setStadiums(response.content);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (err: any) {
      setError(err.message || t("admin_stadium_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, t]);

  useEffect(() => { fetchStadiums(); }, [fetchStadiums]);

  // ── Toolbar handlers ──────────────────────────────────────────
  const handleSearch = () => {
    setCurrentPage(0);
    setFilters(prev => ({ ...prev, search: searchInput }));
  };

  // Clear-X next to the search box: empties the field AND drops the applied search
  const handleClearSearch = () => {
    setSearchInput("");
    setCurrentPage(0);
    setFilters(prev => ({ ...prev, search: "" }));
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(0);
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <FaSort className="sort-icon" />;
    return filters.sortDir === "asc"
      ? <FaSortUp className="sort-icon active" />
      : <FaSortDown className="sort-icon active" />;
  };

  const handleClearFilters = () => {
    setFilters({ search: "", country: "", active: "", isPrimary: "", teamId: "", sortBy: "name", sortDir: "asc" });
    setSearchInput("");
    setCurrentPage(0);
  };

  const hasActiveFilters = !!filters.search || !!filters.country ||
    filters.active !== "" || filters.isPrimary !== "";

  const handlePaginate = (page: number) => setCurrentPage(page - 1);

  // ── Modal helpers ─────────────────────────────────────────────
  const resetForm = () => {
    setFormData({
      teamId: 0, name: "", shortName: "", capacity: "", builtYear: "",
      renovatedYear: "", surface: "", isPrimary: false, city: "", country: "",
      address: "", latitude: "", longitude: "", imageUrl: "", nickname: "", active: true,
    });
    setTeamSearch("");
    setSelectedTeam(null);
    setModalError(null);
  };

  const openCreateModal = () => { resetForm(); setModalType("create"); };

  const openEditModal = (s: AdminStadiumModel) => {
    setSelectedStadium(s);
    setFormData({
      teamId:        s.teamId,
      name:          s.name,
      shortName:     s.shortName      ?? "",
      capacity:      s.capacity       ?? "",
      builtYear:     s.builtYear      ?? "",
      renovatedYear: s.renovatedYear  ?? "",
      surface:       (s.surface as AdminStadiumSurface) ?? "",
      isPrimary:     s.isPrimary,
      city:          s.city           ?? "",
      country:       s.country        ?? "",
      address:       s.address        ?? "",
      latitude:      s.latitude       ?? "",
      longitude:     s.longitude      ?? "",
      imageUrl:      s.imageUrl       ?? "",
      nickname:      s.nickname       ?? "",
      active:        s.active,
    });
    // Pre-fill team search display
    setTeamSearch(s.teamDisplayName);
    setSelectedTeam(allTeams.find(t => t.id === s.teamId) ?? null);
    setModalError(null);
    setModalType("edit");
  };

  const openDeleteModal = (s: AdminStadiumModel) => {
    setSelectedStadium(s);
    setModalError(null);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedStadium(null);
    setModalError(null);
  };

  const selectTeam = (team: TeamModel) => {
    setSelectedTeam(team);
    setTeamSearch(team.displayName ?? team.clubShortName);
    setShowTeamDropdown(false);
    setFormData(p => ({ ...p, teamId: team.id }));
  };

  const clearTeamSelection = () => {
    setSelectedTeam(null);
    setTeamSearch("");
    setFormData(p => ({ ...p, teamId: 0 }));
  };

  // Shared onChange for the non-negative numeric fields (capacity, builtYear,
  // renovatedYear) — rejects the keystroke outright if it would make the value
  // negative, so "-" can never even land in the input, not just at submit time.
  // Empty string is always allowed (field left blank).
  const handleNonNegativeIntChange = (
    field: "capacity" | "builtYear" | "renovatedYear"
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "" || Number(val) >= 0) {
      setFormData(p => ({ ...p, [field]: val }));
    }
  };

  // ── CRUD handlers ─────────────────────────────────────────────
  const handleCreate = async () => {
    if (!formData.teamId)         { setModalError(t("admin_stadium_team_required")); return; }
    if (!formData.name.trim())    { setModalError(t("admin_stadium_name_required")); return; }
    if (formData.capacity !== "" && Number(formData.capacity) < 0) { setModalError(t("admin_stadium_capacity_min")); return; }
    try {
      const req: CreateAdminStadiumRequest = {
        teamId:       formData.teamId,
        name:         formData.name.trim(),
        shortName:    formData.shortName.trim()    || undefined,
        capacity:     formData.capacity !== ""     ? Number(formData.capacity)      : undefined,
        builtYear:    formData.builtYear !== ""    ? Number(formData.builtYear)     : undefined,
        renovatedYear: formData.renovatedYear !== "" ? Number(formData.renovatedYear) : undefined,
        surface:      formData.surface             || undefined,
        isPrimary:    formData.isPrimary,
        city:         formData.city.trim()         || undefined,
        country:      formData.country             || undefined,
        address:      formData.address.trim()      || undefined,
        latitude:     formData.latitude !== ""     ? Number(formData.latitude)      : undefined,
        longitude:    formData.longitude !== ""    ? Number(formData.longitude)     : undefined,
        imageUrl:     formData.imageUrl.trim()     || undefined,
        nickname:     formData.nickname.trim()     || undefined,
      };
      await createAdminStadium(req);
      setSuccessMessage(t("admin_stadium_created"));
      closeModal();
      fetchStadiums();
    } catch (err: any) {
      setModalError(err.message || t("admin_stadium_create_error"));
    }
  };

  const handleUpdate = async () => {
    if (!selectedStadium)      return;
    if (!formData.name.trim()) { setModalError(t("admin_stadium_name_required")); return; }
    if (formData.capacity !== "" && Number(formData.capacity) < 0) { setModalError(t("admin_stadium_capacity_min")); return; }
    try {
      const req: UpdateAdminStadiumRequest = {
        name:         formData.name.trim()         || undefined,
        shortName:    formData.shortName.trim()    || undefined,
        capacity:     formData.capacity !== ""     ? Number(formData.capacity)      : undefined,
        builtYear:    formData.builtYear !== ""    ? Number(formData.builtYear)     : undefined,
        renovatedYear: formData.renovatedYear !== "" ? Number(formData.renovatedYear) : undefined,
        surface:      formData.surface             || undefined,
        isPrimary:    formData.isPrimary,
        city:         formData.city.trim()         || undefined,
        country:      formData.country             || undefined,
        address:      formData.address.trim()      || undefined,
        latitude:     formData.latitude !== ""     ? Number(formData.latitude)      : undefined,
        longitude:    formData.longitude !== ""    ? Number(formData.longitude)     : undefined,
        imageUrl:     formData.imageUrl.trim()     || undefined,
        nickname:     formData.nickname.trim()     || undefined,
        active:       formData.active,
      };
      await updateAdminStadium(selectedStadium.id, req);
      setSuccessMessage(t("admin_stadium_updated"));
      closeModal();
      fetchStadiums();
    } catch (err: any) {
      setModalError(err.message || t("admin_stadium_update_error"));
    }
  };

  const handleDelete = async () => {
    if (!selectedStadium) return;
    try {
      await deleteAdminStadium(selectedStadium.id);
      setSuccessMessage(t("admin_stadium_deleted"));
      closeModal();
      fetchStadiums();
    } catch (err: any) {
      setModalError(err.message || t("admin_stadium_delete_error"));
    }
  };

  const handleToggleActive = async (s: AdminStadiumModel) => {
    try {
      await toggleStadiumActive(s.id, !s.active);
      setSuccessMessage(!s.active ? t("admin_toggle_activated", { name: s.name }) : t("admin_toggle_deactivated", { name: s.name }));
      fetchStadiums();
    } catch (err: any) {
      setError(err.message || t("admin_toggle_error"));
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="admin-stadiums">

      <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />

      {/* Toolbar — reuses shared classes from AdminClubs.css */}
      <div className="users-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder={t("admin_stadium_search_placeholder")}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={handleClearSearch}
            />
          )}
          <button className="search-btn" onClick={handleSearch}><FaSearch /></button>
        </div>

        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> {t("admin_filters")}
          <FaChevronDown className={`chevron ${showFilters ? "open" : ""}`} />
        </button>

        {hasActiveFilters && (
          <button className="clear-filters" onClick={handleClearFilters}>
            <FaTimes /> {t("admin_clear_filters")}
          </button>
        )}

        <button className="create-club-btn" onClick={openCreateModal}>
          <FaPlus /> {t("admin_stadium_add")}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>{t("admin_country")}</label>
            <select value={filters.country} onChange={e => { setFilters(p => ({ ...p, country: e.target.value })); setCurrentPage(0); }}>
              <option value="">{t("admin_all_countries")}</option>
              {countries.map(c => <option key={c.code} value={c.code}>{c.code} — {c.displayName}</option>)}
            </select>
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_status_label")}</label>
            <select value={filters.active} onChange={e => { setFilters(p => ({ ...p, active: e.target.value as any })); setCurrentPage(0); }}>
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_active")}</option>
              <option value="false">{t("admin_inactive")}</option>
            </select>
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_stadium_col_type")}</label>
            <select value={filters.isPrimary} onChange={e => { setFilters(p => ({ ...p, isPrimary: e.target.value as any })); setCurrentPage(0); }}>
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_stadium_primary_full")}</option>
              <option value="false">{t("admin_stadium_secondary_full")}</option>
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="clubs-table-container">
        {loading ? (
          <div className="clubs-loading">{t("loading")}</div>
        ) : error && stadiums.length === 0 ? (
          <AdminErrorBanner message={error} />
        ) : stadiums.length === 0 ? (
          <div className="clubs-empty">{t("admin_stadium_not_found")}</div>
        ) : (
          <table className="clubs-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("id")}>ID {getSortIcon("id")}</th>
                <th>Logo</th>
                <th onClick={() => handleSort("name")}>{t("admin_stadium_col_name")} {getSortIcon("name")}</th>
                <th>{t("admin_stadium_col_team")}</th>
                <th onClick={() => handleSort("capacity")}>{t("admin_stadium_col_capacity")} {getSortIcon("capacity")}</th>
                <th onClick={() => handleSort("builtYear")}>{t("admin_stadium_col_year")} {getSortIcon("builtYear")}</th>
                <th>{t("admin_stadium_col_surface")}</th>
                <th onClick={() => handleSort("city")}>{t("admin_city")} {getSortIcon("city")}</th>
                <th onClick={() => handleSort("country")}>{t("admin_country")} {getSortIcon("country")}</th>
                <th>{t("admin_stadium_col_type")}</th>
                <th onClick={() => handleSort("active")}>{t("admin_status_label")} {getSortIcon("active")}</th>
                <th>{t("admin_col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {stadiums.map(s => (
                <tr key={s.id} className={!s.active ? "row-inactive" : ""}>
                  <td className="club-id">#{s.id}</td>

                  {/* Club logo */}
                  <td style={{ textAlign: "center" }}>
                    {s.clubLogoUrl ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${s.clubLogoUrl}`}
                        alt={s.clubShortName}
                        className="club-logo-thumb"
                        style={{ cursor: "zoom-in" }}
                        onClick={() => setLightboxUrl(`${process.env.REACT_APP_API_URL || "http://localhost:8080"}${s.clubLogoUrl}`)}
                        onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="club-logo-placeholder"><FaBuilding /></div>
                    )}
                  </td>

                  {/* Name + nickname */}
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontWeight: "var(--font-weight-medium)" }}>{s.name}</span>
                      {s.nickname && (
                        <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-secondary)", fontStyle: "italic" }}>
                          {s.nickname}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Team */}
                  <td>
                    {/* Stadiums are linked to TEAMS (teamId) — keep the "Takım" header
                        and link the name to the team page when it is resolvable from
                        the already-loaded allTeams list. */}
                    {(() => {
                      const linkedTeam = allTeams.find(t2 => t2.id === s.teamId);
                      return linkedTeam ? (
                        <Link
                          to={`/takim/${linkedTeam.clubSlug}/${getTeamUrlSlug(linkedTeam)}`}
                          className="text-muted-cell"
                        >
                          {s.teamDisplayName}
                        </Link>
                      ) : (
                        <span className="text-muted-cell">{s.teamDisplayName}</span>
                      );
                    })()}
                  </td>

                  {/* Capacity */}
                  <td style={{ textAlign: "right" }}>
                    {s.capacity ? s.capacity.toLocaleString("tr-TR") : <span className="text-muted-cell">—</span>}
                  </td>

                  {/* Built year */}
                  <td style={{ textAlign: "center" }}>
                    {s.builtYear ?? <span className="text-muted-cell">—</span>}
                  </td>

                  {/* Surface — uses surface-badge + surface-{type} classes from AdminStadiums.css */}
                  <td>
                    {s.surface
                      ? <span className={surfaceBadgeClass(s.surface)}>{t(surfaceLabel(s.surface) ?? "")}</span>
                      : <span className="text-muted-cell">—</span>
                    }
                  </td>

                  {/* City */}
                  <td><span className="text-muted-cell">{s.city ?? "—"}</span></td>

                  {/* Country */}
                  <td>
                    {s.country
                      ? <span className="club-country-badge"><FaMapMarkerAlt className="country-icon" /> {s.country}</span>
                      : <span className="text-muted-cell">—</span>
                    }
                  </td>

                  {/* Primary badge — uses primary-badge / secondary-badge from AdminStadiums.css */}
                  <td style={{ textAlign: "center" }}>
                    {s.isPrimary
                      ? <span className="primary-badge"><FaStar /> {t("admin_stadium_primary")}</span>
                      : <span className="secondary-badge">{t("admin_stadium_secondary")}</span>
                    }
                  </td>

                  {/* Active toggle — reuses active-toggle-btn from AdminClubs.css */}
                  <td>
                    <button
                      className={`active-toggle-btn ${s.active ? "is-active" : "is-inactive"}`}
                      onClick={() => handleToggleActive(s)}
                    >
                      {s.active ? <><FaToggleOn /> {t("admin_active")}</> : <><FaToggleOff /> {t("admin_inactive")}</>}
                    </button>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="action-buttons">
                      <button className="action-btn edit" title={t("admin_edit_btn")} onClick={() => openEditModal(s)}><FaEdit /></button>
                      {isAdmin && (
                        <button className="action-btn danger" title={t("admin_delete_btn")} onClick={() => openDeleteModal(s)}><FaTrash /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="clubs-pagination">
          <span className="page-info">
            {t("admin_stadium_page_info", { current: currentPage + 1, total: totalPages, items: totalItems })}
          </span>
          <Pagination
            currentPage={currentPage + 1}
            totalPages={totalPages}
            totalItems={totalItems}
            paginate={handlePaginate}
          />
        </div>
      )}

      {/* ==================== MODALS ==================== */}

      {/* Create / Edit Modal */}
      {(modalType === "create" || modalType === "edit") && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal club-form-modal">
            <div className="admin-modal-header">
              <h3>{modalType === "create" ? t("admin_stadium_create_title") : t("admin_stadium_edit_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>

            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              {/* Team picker — only on create */}
              {modalType === "create" && (
                <div className="form-row">
                  <div className="form-group" style={{ position: "relative" }}>
                    <label>{t("admin_stadium_col_team")} *</label>
                    <input
                      type="text"
                      placeholder={t("admin_stadium_team_search_placeholder")}
                      value={teamSearch}
                      maxLength={ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH}
                      onChange={e => { setTeamSearch(e.target.value); setSelectedTeam(null); setShowTeamDropdown(true); }}
                      onFocus={() => setShowTeamDropdown(true)}
                      onBlur={() => setTimeout(() => setShowTeamDropdown(false), DROPDOWN_BLUR_DELAY_MS)}
                    />
                    {selectedTeam && (
                      <button
                        type="button"
                        onClick={clearTeamSelection}
                        style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)" }}
                      >
                        <FaTimes />
                      </button>
                    )}
                    {showTeamDropdown && filteredTeams.length > 0 && (
                      <div className="club-dropdown">
                        {filteredTeams.map(team => (
                          <div key={team.id} className="club-dropdown-item" onMouseDown={() => selectTeam(team)}>
                            <span className="club-dropdown-name">{team.displayName ?? team.clubShortName}</span>
                            <span className="club-dropdown-meta">{team.sportType} · {team.gender} · {team.clubCountry}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <AdminCharCounter value={teamSearch} max={ADMIN_TEAM_DISPLAY_NAME_MAX_LENGTH} />
                  </div>
                </div>
              )}

              {/* Name + short name */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_stadium_name_label")} *</label>
                  <input type="text" placeholder='örn: "Tüpraş Stadyumu"' value={formData.name}
                    maxLength={ADMIN_STADIUM_NAME_MAX_LENGTH}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
                  <AdminCharCounter value={formData.name} max={ADMIN_STADIUM_NAME_MAX_LENGTH} />
                </div>
                <div className="form-group">
                  <label>{t("admin_stadium_short_name")}</label>
                  <input type="text" placeholder='örn: "Tüpraş"' value={formData.shortName}
                    maxLength={ADMIN_STADIUM_SHORT_NAME_MAX_LENGTH}
                    onChange={e => setFormData(p => ({ ...p, shortName: e.target.value }))} />
                  <AdminCharCounter value={formData.shortName} max={ADMIN_STADIUM_SHORT_NAME_MAX_LENGTH} />
                </div>
              </div>

              {/* Nickname — full width */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_stadium_nickname")}</label>
                  <input type="text" placeholder='örn: "Vodafone Park"' value={formData.nickname}
                    maxLength={ADMIN_STADIUM_NICKNAME_MAX_LENGTH}
                    onChange={e => setFormData(p => ({ ...p, nickname: e.target.value }))} />
                  <AdminCharCounter value={formData.nickname} max={ADMIN_STADIUM_NICKNAME_MAX_LENGTH} />
                </div>
              </div>

              {/* Capacity + built year + renovated year — 3 columns */}
              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>{t("admin_stadium_capacity_label")}</label>
                  <input type="number" placeholder="örn: 42000" value={formData.capacity}
                    min={0}
                    onChange={handleNonNegativeIntChange("capacity")} />
                </div>
                <div className="form-group">
                  <label>{t("admin_stadium_built_year")}</label>
                  <input type="number" placeholder="örn: 2016" value={formData.builtYear}
                    min={0}
                    onChange={handleNonNegativeIntChange("builtYear")} />
                </div>
                <div className="form-group">
                  <label>{t("admin_stadium_renovated_year")}</label>
                  <input type="number" placeholder="örn: 2021" value={formData.renovatedYear}
                    min={0}
                    onChange={handleNonNegativeIntChange("renovatedYear")} />
                </div>
              </div>

              {/* Surface + country + city — 3 columns */}
              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>{t("admin_stadium_surface_label")}</label>
                  <select value={formData.surface} onChange={e => setFormData(p => ({ ...p, surface: e.target.value as any }))}>
                    <option value="">{t("admin_select")}</option>
                    {SURFACE_OPTIONS.map(s => <option key={s.value} value={s.value}>{t(s.i18nKey)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("admin_country")}</label>
                  <select value={formData.country} onChange={e => setFormData(p => ({ ...p, country: e.target.value }))}>
                    <option value="">{t("admin_select")}</option>
                    {countries.map(c => <option key={c.code} value={c.code}>{c.code} — {c.displayName}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t("admin_city")}</label>
                  <input type="text" placeholder='örn: "İstanbul"' value={formData.city}
                    maxLength={ADMIN_CITY_MAX_LENGTH}
                    onChange={e => setFormData(p => ({ ...p, city: e.target.value }))} />
                  <AdminCharCounter value={formData.city} max={ADMIN_CITY_MAX_LENGTH} />
                </div>
              </div>

              {/* Address — full width */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_stadium_address")}</label>
                  <input type="text" placeholder="Tam adres" value={formData.address}
                    maxLength={ADMIN_STADIUM_ADDRESS_MAX_LENGTH}
                    onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} />
                  <AdminCharCounter value={formData.address} max={ADMIN_STADIUM_ADDRESS_MAX_LENGTH} />
                </div>
              </div>

              {/* Lat + lng */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_stadium_latitude")}</label>
                  <input type="number" step="any" placeholder="örn: 41.0038" value={formData.latitude}
                    onChange={e => setFormData(p => ({ ...p, latitude: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>{t("admin_stadium_longitude")}</label>
                  <input type="number" step="any" placeholder="örn: 29.0042" value={formData.longitude}
                    onChange={e => setFormData(p => ({ ...p, longitude: e.target.value }))} />
                </div>
              </div>

              {/* Image URL */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t("admin_stadium_image_url")}</label>
                  <input type="text" placeholder="https://..." value={formData.imageUrl}
                    maxLength={ADMIN_URL_MAX_LENGTH}
                    onChange={e => setFormData(p => ({ ...p, imageUrl: e.target.value }))} />
                  <AdminCharCounter value={formData.imageUrl} max={ADMIN_URL_MAX_LENGTH} />
                </div>
              </div>

              {/* Image preview */}
              {formData.imageUrl && (
                <div className="club-logo-preview">
                  <img
                    src={formData.imageUrl as string}
                    alt={t("admin_stadium_image_preview")}
                    style={{ cursor: "zoom-in" }}
                    onClick={() => setLightboxUrl(formData.imageUrl as string)}
                    onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                  <span>{t("admin_stadium_image_preview")}</span>
                </div>
              )}

              {/* isPrimary + active toggles */}
              <div className="form-row">
                <div className="form-group form-group--inline">
                  <label>{t("admin_stadium_primary_full")}</label>
                  <button
                    type="button"
                    className={`active-toggle-btn ${formData.isPrimary ? "is-active" : "is-inactive"}`}
                    onClick={() => setFormData(p => ({ ...p, isPrimary: !p.isPrimary }))}
                  >
                    {formData.isPrimary ? <><FaToggleOn /> {t("admin_yes_toggle")}</> : <><FaToggleOff /> {t("admin_no_toggle")}</>}
                  </button>
                </div>
                {modalType === "edit" && (
                  <div className="form-group form-group--inline">
                    <label>{t("admin_active")}</label>
                    <button
                      type="button"
                      className={`active-toggle-btn ${formData.active ? "is-active" : "is-inactive"}`}
                      onClick={() => setFormData(p => ({ ...p, active: !p.active }))}
                    >
                      {formData.active ? <><FaToggleOn /> {t("admin_active")}</> : <><FaToggleOff /> {t("admin_inactive")}</>}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="modal-confirm-btn" onClick={modalType === "create" ? handleCreate : handleUpdate}>
                {modalType === "create" ? t("admin_create_btn") : t("admin_save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {modalType === "delete" && selectedStadium && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="admin-modal delete-modal">
            <div className="admin-modal-header">
              <h3>{t("admin_stadium_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}><FaTimes /></button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <p>{t("admin_stadium_delete_confirm", { name: selectedStadium.name })}</p>
              <p className="delete-warning-text">{t("admin_confirm_delete_permanent")}</p>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>{t("admin_cancel")}</button>
              <button className="modal-delete-btn" onClick={handleDelete}>{t("admin_delete_btn")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={lightboxUrl}
            alt="Preview"
            style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 8 }}
          />
        </div>
      )}
    </div>
  );
};